// @givmo/board — a committee's practice of suggesting its decisions for the board's next meeting
// (CBG-YR6-CONFIRM; the backend's `board-committee-confirmation.md` §2.1). No frame drew this dialog.
//
// Opened from the committee's page: "Change" beside the practice line while it's on, or "Suggest its
// decisions for the board…" in the "⋯" menu while it's off. One checkbox and, beside "on", where the
// organization wrote its practice down. Turning it off clears that (a citation goes with the practice
// only while it's on). The Owner's act (`board_member:manage`), logged on the committee's log with its
// values; nothing gates on it — it decides only whether a board meeting suggests the decisions.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError, type BoardCommitteeOut } from '@givmo/api-client';
import { Button, Checkbox, Dialog, ErrorMessage, TextField, toast } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { practiceOf } from './committee-confirmation-decode';
import { committeeConfirmationWords as cw } from './committee-confirmation-words';
import { committeesWords as w } from './committees-words';

/** A citation's bound on the wire (`BOARD_COMMITTEE_CONFIRM_CITATION_MAX_LENGTH`). */
const CITATION_MAX = 200;

export function ConfirmationDialog({
  orgRef,
  committee,
  onClose,
  onDone,
}: {
  orgRef: string;
  committee: BoardCommitteeOut;
  onClose: () => void;
  onDone: (updated: BoardCommitteeOut) => void;
}) {
  const operations = useBoardOperations();
  const stored = practiceOf(committee);
  // Ticked to start either way: with the practice on it shows it as it stands, and opened from the
  // menu with it off, the dialog is how it is turned on.
  const [on, setOn] = useState(true);
  const [citation, setCitation] = useState(stored.citation ?? '');
  const [error, setError] = useState<string | null>(null);
  const trimmed = citation.trim();
  const changed =
    on !== stored.on || (on && (trimmed.length > 0 ? trimmed : null) !== stored.citation);

  const save = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerSetBoardCommitteeConfirmation({
        path: { org_ref: orgRef, committee_id: committee.id },
        body: on
          ? { confirm_with_board: true, ...(trimmed.length > 0 ? { citation: trimmed } : {}) }
          : { confirm_with_board: false },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (updated) => {
      toast.success(cw.saved);
      onDone(updated);
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : cw.saveFailed),
  });
  const pending = save.isPending;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onClose()}>
      <Dialog.Content
        aria-label={cw.pageTitle}
        className="flex flex-col gap-16 overflow-y-auto"
        testId="committee-practice-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {cw.pageTitle}
        </Dialog.Title>
        <label className="flex items-start gap-10 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          <Checkbox
            appearance="saas"
            checked={on}
            disabled={pending}
            onCheckedChange={(next) => setOn(next === true)}
            testId="committee-practice-on"
          />
          {cw.confirmLabel}
        </label>
        {on ? (
          <div className="flex flex-col gap-8">
            <TextField
              label={cw.citationLabel}
              value={citation}
              maxLength={CITATION_MAX}
              disabled={pending}
              onChange={(event) => setCitation(event.target.value)}
              testId="committee-practice-citation"
            />
            <FieldNote>{cw.citationHint}</FieldNote>
          </div>
        ) : null}
        {error !== null ? (
          <ErrorMessage testId="committee-practice-error">{error}</ErrorMessage>
        ) : null}
        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={pending}
            onClick={onClose}
            testId="committee-practice-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={pending}
            disabled={pending || !changed}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="committee-practice-save"
          >
            {cw.save}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
